import type {
  CreateTicketRequest,
  ReplyTicketRequest,
  Ticket,
  TicketAttachment,
  TicketMessage,
  TicketStatus,
} from '@/types/admin';
import { api } from './client';

const BASE_PATH = '/v1/tickets'
const ADMIN_PATH = '/v1/admin/tickets'

interface TicketWithMessages {
  ticket: Ticket;
  messages: TicketMessage[];
}

/** Build query string from a params object, omitting undefined/empty values */
function buildQuery(params?: Record<string, string | number | undefined>): string {
  if (!params) return ''
  const qs = new URLSearchParams()
  for (const [key, val] of Object.entries(params)) {
    if (val !== undefined && val !== '') qs.set(key, String(val))
  }
  const str = qs.toString()
  return str ? `?${str}` : ''
}

export interface TicketCounts {
  open: number
  inProgress: number
  total: number
  /** Snapshot of each ticket's last update — used to detect new messages */
  snapshots: Array<{ id: string; updatedAt: string; subject: string; lastSenderType?: string }>
}

export const ticketApi = {
  // User-facing: GET /api/v1/tickets → Ticket[]
  myTickets: async () => {
    const resp = await api.get<Ticket[]>(BASE_PATH)
    return { tickets: resp }
  },

  createTicket: (data: CreateTicketRequest) =>
    api.post<Ticket>(BASE_PATH, data),

  // GET /api/v1/tickets/:id → { ticket, messages }
  getTicket: async (ticketId: string) => {
    const resp = await api.get<TicketWithMessages>(`${BASE_PATH}/${ticketId}`)
    return resp.ticket
  },

  getMessages: async (ticketId: string) => {
    const resp = await api.get<TicketWithMessages>(`${BASE_PATH}/${ticketId}`)
    return { messages: resp.messages }
  },

  reply: (ticketId: string, data: ReplyTicketRequest) =>
    api.post<TicketMessage>(`${BASE_PATH}/${ticketId}/reply`, data),

  // User-facing: count open tickets for badge + snapshots for message detection
  myCounts: async (): Promise<TicketCounts> => {
    const resp = await api.get<Ticket[]>(BASE_PATH)
    const open = resp.filter((t) => t.status === 'open').length
    const inProgress = resp.filter((t) => t.status === 'in_progress').length
    const snapshots = resp.map((t) => ({ id: t.id, updatedAt: t.updatedAt, subject: t.subject, lastSenderType: t.lastSenderType }))
    return { open, inProgress, total: resp.length, snapshots }
  },

  // Admin-only: GET /api/v1/admin/tickets → Ticket[]
  listAll: async (params?: { status?: string; category?: string; page?: number; limit?: number }) => {
    const query = buildQuery(params)
    const resp = await api.get<Ticket[]>(`${ADMIN_PATH}${query}`)
    return { tickets: resp }
  },

  // Admin-only: count open tickets for badge + snapshots for message detection
  adminCounts: async (): Promise<TicketCounts> => {
    const resp = await api.get<Ticket[]>(ADMIN_PATH)
    const open = resp.filter((t) => t.status === 'open').length
    const inProgress = resp.filter((t) => t.status === 'in_progress').length
    const snapshots = resp.map((t) => ({ id: t.id, updatedAt: t.updatedAt, subject: t.subject, lastSenderType: t.lastSenderType }))
    return { open, inProgress, total: resp.length, snapshots }
  },

  updateStatus: (ticketId: string, status: TicketStatus) =>
    api.patch<void>(`${ADMIN_PATH}/${ticketId}/status`, { status }),

  adminReply: (ticketId: string, data: ReplyTicketRequest) =>
    api.post<TicketMessage>(`${ADMIN_PATH}/${ticketId}/reply`, data),

  adminGetTicket: async (ticketId: string) => {
    const resp = await api.get<TicketWithMessages>(`${ADMIN_PATH}/${ticketId}`)
    return resp.ticket
  },

  adminGetMessages: async (ticketId: string) => {
    const resp = await api.get<TicketWithMessages>(`${ADMIN_PATH}/${ticketId}`)
    return { messages: resp.messages }
  },

  // Attachment upload/delete (used by both user and admin)
  uploadImage: async (base64Data: string): Promise<TicketAttachment> => {
    const resp = await api.post<TicketAttachment>(`${BASE_PATH}/attachments/upload`, {
      data: base64Data,
    })
    return resp
  },

  deleteImage: async (storageKey: string): Promise<void> => {
    await api.post<void>(`${BASE_PATH}/attachments/delete`, {
      storageKey,
    })
  },
}
